import { Check, Flag } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { AutoBatchResponse, ColdStartTier } from "@opus-dt/shared-types";
import { ProvenanceStrip } from "./charts/ProvenanceStrip";

// Ordem = hierarquia de evidência (research D4): product vence similar, perde p/ measured —
// espelha o tierRank do backend (auto-batch.derive.ts) e o merge de applyPairProfile.
const TIER_META: Record<ColdStartTier, { order: number; label: string; desc: string }> = {
  measured: { order: 1, label: "Histórico real", desc: "Comportamento medido da própria máquina." },
  // Valor autorado por par (máquina×produto).
  product: {
    order: 2,
    label: "Perfil por produto",
    desc: "Parâmetro autorado para este produto na máquina.",
  },
  similar: {
    order: 3,
    label: "Máquina semelhante",
    desc: "Histórico emprestado de máquina indicada.",
  },
  spec: { order: 4, label: "Spec nominal", desc: "Capacidade/ritmo de especificação." },
  manual: { order: 5, label: "Premissa manual", desc: "Fallback conservador, sem dado." },
};

interface ColdStartPanelProps {
  batch: AutoBatchResponse;
  onIndicate?: (machineId: string, machineName: string) => void;
}

// Premissas e cold start: tier + rótulo medido/premissa por máquina.
export function ColdStartPanel({ batch, onIndicate }: ColdStartPanelProps) {
  if (batch.coldStart.length === 0) return null;
  const assumedCount = batch.coldStart.filter((m) => m.assumed).length;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Premissas e cold start</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        <p className="text-sm text-muted-foreground">
          De onde veio o dado de cada máquina. Sem histórico, o sistema não inventa — desce na
          cascata e rebaixa a confiança.
        </p>
        {/* A lista responde "esta máquina tem histórico?"; a faixa responde "posso
            confiar nesta rodada?", que é a pergunta que vem antes de qualquer decisão. */}
        <ProvenanceStrip entries={batch.coldStart} />
        <p className="pb-1 text-[12px] leading-relaxed text-muted-foreground">
          {assumedCount === 0 ? (
            <>
              <b className="text-rd-success-700">
                {batch.coldStart.length} de {batch.coldStart.length} máquinas com histórico medido
              </b>{" "}
              — o motor não precisou assumir nada.
            </>
          ) : (
            <>
              <b className="text-rd-warning-700">
                {assumedCount} de {batch.coldStart.length}{" "}
                {assumedCount === 1 ? "máquina depende" : "máquinas dependem"} de premissa
              </b>{" "}
              — o resultado carrega essa incerteza.
            </>
          )}
        </p>
        {batch.coldStart.map((m) => {
          const tier = TIER_META[m.tier];
          return (
            <div
              key={m.machineId}
              className="flex items-center gap-2 border-b border-border-subtle py-1.5 last:border-0"
              title={tier.desc}
            >
              <span className={m.assumed ? "text-amber-600" : "text-emerald-600"}>
                {m.assumed ? <Flag className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] font-medium">
                {m.machineName}
              </span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{tier.label}</span>
              <span
                className={cn(
                  "shrink-0 rounded px-1.5 text-[10px] font-medium",
                  m.assumed ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                )}
              >
                {m.assumed ? "premissa" : "medido"}
              </span>
              {m.assumed && onIndicate ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onIndicate(m.machineId, m.machineName)}
                >
                  Indicar semelhante
                </Button>
              ) : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
